import React from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable, LoadingSpinner, columnTypes, pitchTypeDisplayName, ratio, styles } from 'yankees-react-utils';
import styled from 'styled-components';
import ReportCard from '../../../../components/ReportCard';

const StyledReportCard = styled(ReportCard)`
    .table {
        text-align: center;
    }
    .usage-cell {
        border-left: 1px solid rgb(173, 176, 178) !important;
    }
    .kzone-cell {
        border-left: 1px dashed rgb(173, 176, 178) !important;
    }
    .high-usage, .low-usage {
        font-weight: bold !important;
    }
    .high-usage {
        background-color: #AACCFF !important;
    }
    .low-usage {
        background-color: #F2B9B4 !important;
    }
`;

const getUsageClass = (pPct, lgPct, threshold, includeLowUsage) => {
    let usageClass;
    if (pPct >= (lgPct + threshold)) {
        usageClass = 'high-usage';
    } else if (includeLowUsage && pPct <= (lgPct - threshold)) {
        usageClass = 'low-usage';
    }
    return usageClass;
};

const pitchFreq = (lgavgkzon, numFbInRepertoire, stat, pitchUsage, statKey, lgavgKey) => {
    if ((stat.pitches_total ? ratio(stat.pitches, stat.pitches_total) : 0) < 0.1) return '';

    let usageClass = '';
    if (!pitchUsage) return usageClass;

    const lookupKey = lgavgKey === undefined ? statKey : lgavgKey;
    if (!stat.isfastball) {
        const pitchTypeUsageAll = stat.pitches / (stat.pitches_total || stat.totalpitches);
        if (pitchUsage > (pitchTypeUsageAll + 0.10)) {
            usageClass = 'high-usage';
        }
    } else if (stat.isfastball && statKey === 'cnt32' && pitchUsage >= 0.945) {
        usageClass = 'high-usage';
    } else if (stat.isfastball) {
        const fxtype = numFbInRepertoire === 1 ? 1 : stat.fxtype;
        if (fxtype === 1) {
            const lgAvgCt = lgavgkzon.find((avg) => avg.fxtype === fxtype);
            const lgAvgAll = lgavgkzon.find((avg) => avg.fxtype === null);
            if (lgAvgCt && lgAvgAll && lgAvgAll[`t${lookupKey}`] > 0) {
                usageClass = getUsageClass(pitchUsage, lgAvgCt[`t${lookupKey}`] / lgAvgAll[`t${lookupKey}`], 0.10);
            }
        }
    }
    return usageClass;
};

const kzoneAccuracy = (usage, lgavgkzon, kAccuracy, lgavgKey, fxtype) => {
    if (usage < 0.1) return '';

    let accuracyClass = '';
    const lgAvg = lgavgkzon.find((avg) => avg.fxtype === fxtype || (avg.fxtype === null && !fxtype));
    if (kAccuracy > 0 && lgAvg && lgAvg[`t${lgavgKey}`] > 0) {
        const pEst = lgAvg[`kzon${lgavgKey}`] / lgAvg[`t${lgavgKey}`];
        const stdDev = Math.sqrt(pEst * (1 - pEst) / 200);
        accuracyClass = getUsageClass(kAccuracy, pEst, 2 * stdDev, true);
    }
    return accuracyClass;
};

const getColumns = (lgavgkzon, numFbInRepertoire, isStartingPitcher, translate) => {
    const returnCols = [];
    returnCols.push({ id: 'title',
        title: '',
        headerClass: 'usage-cell',
        cellClass: () => 'usage-cell',
        transform: (row) => pitchTypeDisplayName(row, true, true, translate),
        type: columnTypes.TEXT },
    {
        id: 'pit%',
        title: '',
        transform: (row) => ratio(row.pitches, row.pitches_total),
        type: columnTypes.PERCENT,
        headerClass: 'usage-cell',
        cellClass: 'usage-cell',
    },
    {
        id: 'kzon%',
        title: '',
        transform: (row) => ratio(row.pstrike, row.pitches),
        type: columnTypes.PERCENT,
        cellClass: (r, v) => `${kzoneAccuracy(r.pitches_total ? ratio(r.pitches, r.pitches_total) : 0, lgavgkzon, v, 'pit', r.fxtype)} usage-cell`,
        headerClass: 'usage-cell',
    },
    {
        id: '00%Usage',
        title: translate('sections.opponent.player.use'),
        headerClass: 'usage-cell',
        cellClass: (r, v) => `${pitchFreq(lgavgkzon, numFbInRepertoire, r, v, 'firstpit', '00')} usage-cell`,
        transform: (row) => ratio(row.firstpit, row.firstpit_total),
        type: columnTypes.PERCENT,
    },
    {
        id: '00%Kzone',
        title: translate('baseball.abbr-strikezone'),
        cellClass: (r, v) => `${ratio(r.firstpit, r.firstpit_total) > 0.1
            ? kzoneAccuracy(r.pitches_total ? ratio(r.pitches, r.pitches_total) : 0, lgavgkzon, v, '00', r.fxtype) : ''} kzone-cell`,
        headerClass: 'kzone-cell',
        transform: (row) => ratio(row.firstpitkzone, row.firstpit),
        type: columnTypes.PERCENT,
    },
    {
        id: 'HitterAheadUsage',
        title: translate('sections.opponent.player.use'),
        headerClass: 'usage-cell',
        cellClass: (r, v) => `${pitchFreq(lgavgkzon, numFbInRepertoire, r, v, 'ahead')} usage-cell`,
        transform: (row) => ratio(row.ahead, row.ahead_total),
        type: columnTypes.PERCENT,
    },
    {
        id: 'HitterAheadKZone',
        title: translate('baseball.abbr-strikezone'),
        cellClass: (r, v) => `${ratio(r.ahead, r.ahead_total) > 0.1
            ? kzoneAccuracy(r.pitches_total ? ratio(r.pitches, r.pitches_total) : 0, lgavgkzon, v, 'ahead', r.fxtype) : ''} kzone-cell`,
        headerClass: 'kzone-cell',
        transform: (row) => ratio(row.aheadkzone, row.ahead),
        type: columnTypes.PERCENT,
    },
    {
        id: '2KUsage',
        title: translate('sections.opponent.player.use'),
        headerClass: 'usage-cell',
        cellClass: (r, v) => `${pitchFreq(lgavgkzon, numFbInRepertoire, r, v, 'twostrikes', '2k')} usage-cell`,
        transform: (row) => ratio(row.twostrikes, row.twostrikes_total),
        type: columnTypes.PERCENT,
    },
    {
        id: '2KKZone',
        title: translate('baseball.abbr-strikezone'),
        cellClass: (r, v) => `${ratio(r.twostrikes, r.twostrikes_total) > 0.1
            ? kzoneAccuracy(r.pitches_total ? ratio(r.pitches, r.pitches_total) : 0, lgavgkzon, v, '2k', r.fxtype) : ''} kzone-cell`,
        headerClass: 'kzone-cell',
        transform: (row) => ratio(row.twostrikeskzone, row.twostrikes),
        type: columnTypes.PERCENT,
    },
    {
        id: '32Usage',
        title: translate('sections.opponent.player.use'),
        headerClass: 'usage-cell',
        cellClass: (r, v) => `${pitchFreq(lgavgkzon, numFbInRepertoire, r, v, 'cnt32', '32')} usage-cell`,
        transform: (row) => ratio(row.cnt32, row.cnt32_total),
        type: columnTypes.PERCENT,
    },
    {
        id: '32KZone',
        title: translate('baseball.abbr-strikezone'),
        cellClass: (r, v) => `${ratio(r.cnt32, r.cnt32_total) > 0.1
            ? kzoneAccuracy(r.pitches_total ? ratio(r.pitches, r.pitches_total) : 0, lgavgkzon, v, '32', r.fxtype) : ''} kzone-cell`,
        headerClass: 'kzone-cell',
        transform: (row) => ratio(row.cnt32kzone, row.cnt32),
        type: columnTypes.PERCENT,
    },
    {
        id: 'RISPUsage',
        title: translate('sections.opponent.player.use'),
        headerClass: 'usage-cell',
        cellClass: (r, v) => `${pitchFreq(lgavgkzon, numFbInRepertoire, r, v, 'risp')} usage-cell`,
        transform: (row) => ratio(row.risp, row.risp_total),
        type: columnTypes.PERCENT,
    },
    {
        id: 'RISPKZone',
        title: translate('baseball.abbr-strikezone'),
        cellClass: (r, v) => `${ratio(r.risp, r.risp_total) > 0.1
            ? kzoneAccuracy(r.pitches_total ? ratio(r.pitches, r.pitches_total) : 0, lgavgkzon, v, 'risp', r.fxtype) : ''} kzone-cell`,
        headerClass: 'kzone-cell',
        transform: (row) => ratio(row.rispkzone, row.risp),
        type: columnTypes.PERCENT,
    },
    {
        id: 'RISP00Usage',
        title: translate('sections.opponent.player.use'),
        headerClass: 'usage-cell',
        cellClass: (r, v) => `${pitchFreq(lgavgkzon, numFbInRepertoire, r, v, 'risp00')} usage-cell`,
        transform: (row) => ratio(row.risp00, row.risp00_total),
        type: columnTypes.PERCENT,
    },
    {
        id: 'RISP00KZone',
        title: translate('baseball.abbr-strikezone'),
        cellClass: (r, v) => `${ratio(r.risp00, r.risp00_total) > 0.1
            ? kzoneAccuracy(r.pitches_total ? ratio(r.pitches, r.pitches_total) : 0, lgavgkzon, v, 'risp00', r.fxtype) : ''} kzone-cell`,
        headerClass: 'kzone-cell',
        transform: (row) => ratio(row.risp00kzone, row.risp00),
        type: columnTypes.PERCENT,
    });
    if (isStartingPitcher) {
        returnCols.push(
            {
                id: 'overall_firstusageusage',
                type: columnTypes.PERCENT,
                cellClass: 'usage-cell',
                headerClass: 'usage-cell',
                transform: (row) => ratio(row.tto_first, row.tto_first_total),
            },
            {
                id: 'overall_secondusage',
                type: columnTypes.PERCENT,
                cellClass: 'usage-cell',
                headerClass: 'usage-cell',
                transform: (row) => ratio(row.tto_second, row.tto_second_total),
            },
            {
                id: 'overall_thirdusage',
                type: columnTypes.PERCENT,
                cellClass: 'usage-cell',
                headerClass: 'usage-cell',
                transform: (row) => ratio(row.tto_third, row.tto_third_total),
            },
            {
                id: 'firstpitch_firstusage',
                type: columnTypes.PERCENT,
                cellClass: 'usage-cell',
                headerClass: 'usage-cell',
                transform: (row) => ratio(row.tto_00_first, row.tto_00_first_total),
            },
            {
                id: 'firstpitch_secondusage',
                type: columnTypes.PERCENT,
                cellClass: 'usage-cell',
                headerClass: 'usage-cell',
                transform: (row) => ratio(row.tto_00_second, row.tto_00_second_total),
            },
            {
                id: 'firstpitch_thirdusage',
                type: columnTypes.PERCENT,
                cellClass: 'usage-cell',
                headerClass: 'usage-cell',
                transform: (row) => ratio(row.tto_00_third, row.tto_00_third_total),
            },
        );
    }

    return returnCols;
};

const getRows = (repertoire, timethroughorder) => {
    const returnValue = [];
    repertoire.forEach((pitch) => {
        let targetTTO = timethroughorder.repertoire.filter((x) => x.fxtype === pitch.fxtype && (x.fxsubtype === pitch.fxsubtype || x.fxsubtype === undefined));
        // eslint-disable-next-line prefer-destructuring
        if (targetTTO) targetTTO = targetTTO[0];
        returnValue.push({ ...pitch, ...targetTTO });
    });
    return returnValue;
};

const SuperHeader = ({ isStartingPitcher, translate }) => (
    <>
        <tr className="fw-bold text-uppercase">
            <td style={{ backgroundColor: styles.theme.background.astronaut, borderRight: '1px solid #FFF' }} colSpan={15}>&nbsp;</td>
            {isStartingPitcher && (
                <>
                    <td
                        style={{ backgroundColor: styles.theme.background.astronaut, color: '#FFF', borderRight: '1px solid #FFF', textAlign: 'center' }}
                        colSpan={3}
                    >
                        {translate('sections.reports.pitcher.overall')} {translate('sections.opponent.player.abbr-time-thru-order')}
                    </td>
                    <td
                        style={{ backgroundColor: styles.theme.background.astronaut, color: '#FFF', borderRight: '1px solid #FFF', textAlign: 'center' }}
                        colSpan={3}
                    >
                        0-0 {translate('baseball.count')} {translate('sections.opponent.player.abbr-time-thru-order')}
                    </td>
                </>
            )}
        </tr>
        <tr>
            <th className="usage-cell">{translate('sections.opponent.player.type')}</th>
            <th className="usage-cell">{translate('sections.reports.pitcher.usage-pct')}</th>
            <th className="usage-cell">{translate('sections.reports.pitcher.kzon-pct')}</th>
            <th className="usage-cell" colSpan={2}>0-0</th>
            <th className="usage-cell" colSpan={2}>{translate('sections.opponent.player.hitter-ahead')}</th>
            <th className="usage-cell" colSpan={2}>{translate('sections.opponent.team.abbr-two-strike')}</th>
            <th className="usage-cell" colSpan={2}>3-2</th>
            <th className="usage-cell" colSpan={2}>{translate('baseball.abbr-runners-in-position')}</th>
            <th className="usage-cell" colSpan={2}>0-0 {translate('baseball.abbr-runners-in-position')}</th>
            {isStartingPitcher && (
                <>
                    <th className="usage-cell"><span>{translate('format.number_ordinal_one', { count: 1 })}</span></th>
                    <th className="usage-cell"><span>{translate('format.number_ordinal_two', { count: 2 })}</span></th>
                    <th className="usage-cell"><span>{translate('format.number_ordinal_few', { count: 3 })}</span></th>
                    <th className="usage-cell"><span>{translate('format.number_ordinal_one', { count: 1 })}</span></th>
                    <th className="usage-cell"><span>{translate('format.number_ordinal_two', { count: 2 })}</span></th>
                    <th className="usage-cell"><span>{translate('format.number_ordinal_few', { count: 3 })}</span></th>
                </>
            )}
        </tr>
    </>
);

const PitchUsageTable = ({ isLoading, timeThroughOrder, className, lgavgkzon, numFbInRepertoire, isStartingPitcher, repertoire }) => {
    const { t: translate } = useTranslation();

    if (isLoading || !timeThroughOrder) return <div className="py-5 text-center"><LoadingSpinner /></div>;

    return (
        <StyledReportCard title={translate('pitcher-overview.sections.pitch-usage')} className={className}>
            <DataTable
                columns={getColumns(lgavgkzon, numFbInRepertoire, isStartingPitcher, translate)}
                rows={getRows(repertoire.filter((type) => type.fxtype !== 1), timeThroughOrder)}
                superheader={<SuperHeader isStartingPitcher={isStartingPitcher} translate={translate} />}
            />
        </StyledReportCard>
    );
};

export default PitchUsageTable;
